import React, { useEffect, useState, useRef, useMemo } from 'react';
import {
  Alert,
  Button,
  Checkbox,
  Chip,
  ChipGroup,
  Form,
  FormGroup,
  FormHelperText,
  HelperText,
  HelperTextItem,
  Popover,
  Spinner,
  Switch,
  TextInput,
  Tooltip,
  ValidatedOptions,
} from '@patternfly/react-core';
import { useStableAccessToken } from '@app/GranularDeploy/hooks/useStableAccessToken';
import BranchTypeahead from '@app/GranularDeploy/components/BranchTypeahead';
import { apiBaselineNamespaces, BaselineNamespace } from '../helpers/apiHelpers';
import { useSessionInUse, usePRBranch, usePreviewStop } from '../hooks/usePreviewEnv';
import { useDebounce } from '@app/GranularDeploy/hooks/useDebounce';
import { RegressionTestConfigModal } from './RegressionTestConfigModal';
import { RegressionTestSuiteSelection } from './RegressionTestSuiteSelector';

const TTL_PATTERN = /^\d+[hd]$/;
const GITHUB_PR_URL_PATTERN = /^https?:\/\/github\.com\/[^/]+\/[^/]+\/pull\/\d+/;

export type BranchEntry =
  | { kind: 'pr'; url: string }
  | { kind: 'branch'; name: string };

export interface ConfigureFormValues {
  inputMode: 'pr' | 'branch';
  entries: BranchEntry[];
  /** Resolved branch names (one per entry, undefined while still resolving) */
  resolvedBranches: (string | undefined)[];
  namespace: string;
  previewTag: string;
  ttl: string;
  forceOverride: boolean;
  regressionTestsEnabled: boolean;
  regressionTestSelections: Record<string, RegressionTestSuiteSelection>;
}

interface ConfigureStepProps {
  values: ConfigureFormValues;
  onChange: (values: ConfigureFormValues) => void;
}

// ---------------------------------------------------------------------------
// PrChip: resolves a GitHub PR URL and renders as a removable chip
// ---------------------------------------------------------------------------
const PrChip: React.FC<{
  url: string;
  onResolve: (branch: string | undefined) => void;
  onRemove: () => void;
  onError?: (error: string | null) => void;
}> = ({ url, onResolve, onRemove, onError }) => {
  const { resolution, loading, error } = usePRBranch(
    GITHUB_PR_URL_PATTERN.test(url) ? url : null,
  );

  useEffect(() => {
    onResolve(resolution?.branch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolution]);

  useEffect(() => {
    onError?.(error ?? null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  const urlMatch = url.match(/github\.com\/([^/]+\/[^/]+)\/pull\/(\d+)/);
  const shortLabel = urlMatch
    ? `${urlMatch[1]} #${urlMatch[2]}`
    : url.replace(/^https?:\/\/github\.com\//, '');

  const chipText = loading
    ? `${shortLabel} · Resolving…`
    : error
    ? shortLabel
    : resolution
    ? `${resolution.repo} #${resolution.number} · ${resolution.branch}`
    : shortLabel;

  const chip = (
    <Chip onClick={onRemove} style={error ? { borderColor: 'var(--pf-v5-global--danger-color--100)' } : undefined}>
      {loading && <Spinner size="sm" style={{ marginRight: 4 }} />}
      {chipText}
    </Chip>
  );

  return error ? <Tooltip content={error}>{chip}</Tooltip> : chip;
};

// ---------------------------------------------------------------------------
// ConfigureStep
// ---------------------------------------------------------------------------
export const ConfigureStep: React.FC<ConfigureStepProps> = ({ values, onChange }) => {
  const { entries, resolvedBranches } = values;
  const [inputValue, setInputValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const [prAuthError, setPrAuthError] = useState(false);
  const [isRegressionModalOpen, setIsRegressionModalOpen] = useState(false);

  const handlePrError = (error: string | null) => {
    if (error && /401|unauthorized|login_required|interaction_required/i.test(error)) {
      setPrAuthError(true);
    }
  };

  // --- namespace ---
  const [namespaces, setNamespaces] = useState<BaselineNamespace[]>([]);
  const [loadingNs, setLoadingNs] = useState(false);
  const tokenGetter = useStableAccessToken();

  useEffect(() => {
    setLoadingNs(true);
    apiBaselineNamespaces(tokenGetter)
      .then(setNamespaces)
      .catch(() => setNamespaces([]))
      .finally(() => setLoadingNs(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const namespaceOptions = namespaces.map((ns) => ns.name);

  // --- preview tag uniqueness ---
  const [stopRefreshKey, setStopRefreshKey] = useState(0);
  const { stopPreview, isStopping, stopError } = usePreviewStop();

  const debouncedTag = useDebounce(values.previewTag, 500);
  const { inUse, checking } = useSessionInUse(values.namespace, debouncedTag, stopRefreshKey);

  // Auto-clear forceOverride when the tag is no longer in use (user changed the tag to a free one)
  useEffect(() => {
    if (inUse === false && values.forceOverride) {
      onChange({ ...values, forceOverride: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inUse]);

  const ttlValidated: ValidatedOptions =
    !values.ttl || TTL_PATTERN.test(values.ttl) ? ValidatedOptions.default : ValidatedOptions.error;

  const tagValidated: ValidatedOptions =
    inUse === true && !values.forceOverride ? ValidatedOptions.error : inUse === false ? ValidatedOptions.success : ValidatedOptions.default;

  // --- chip helpers ---
  const addEntry = (raw: string) => {
    const value = raw.trim();
    if (!value) return;
    const newEntry: BranchEntry =
      values.inputMode === 'pr' ? { kind: 'pr', url: value } : { kind: 'branch', name: value };
    onChange({
      ...values,
      entries: [...entries, newEntry],
      resolvedBranches: [...resolvedBranches, values.inputMode === 'branch' ? value : undefined],
    });
    setInputValue('');
  };

  const removeEntry = (idx: number) => {
    onChange({
      ...values,
      entries: entries.filter((_, i) => i !== idx),
      resolvedBranches: resolvedBranches.filter((_, i) => i !== idx),
    });
  };

  const handleResolve = (idx: number, branch: string | undefined) => {
    const newResolved = resolvedBranches.map((r, i) => (i === idx ? branch : r));
    onChange({ ...values, resolvedBranches: newResolved });
  };

  const switchMode = (mode: 'pr' | 'branch') => {
    onChange({ ...values, inputMode: mode, entries: [], resolvedBranches: [] });
    setInputValue('');
  };

  const isPrMode = values.inputMode === 'pr';

  // Detect duplicate repos: two or more entries targeting the same org/repo.
  // Only one service image can win per repo — warn the user.
  const duplicateRepos = useMemo(() => {
    const repoCounts = new Map<string, number>();
    for (const entry of entries) {
      const repo = entry.kind === 'pr'
        ? (entry.url.match(/github\.com\/([^/]+\/[^/]+)\/pull\/\d+/)?.[1] ?? null)
        : null; // branch mode: no repo info available
      if (repo) repoCounts.set(repo, (repoCounts.get(repo) ?? 0) + 1);
    }
    return Array.from(repoCounts.entries())
      .filter(([, count]) => count > 1)
      .map(([repo]) => repo);
  }, [entries]);

  return (
    <Form isHorizontal>
      {/* ---- PR URLs / Branch chip input ---- */}
      <FormGroup
        label={
          <Popover
            triggerAction="hover"
            headerContent={isPrMode ? 'Pull Request URLs' : 'Feature Branches'}
            bodyContent={
              isPrMode
                ? 'Paste GitHub PR URLs for the branches you want to preview. Ranch resolves each PR to its head branch and finds the matching Docker image built from that branch.'
                : 'Enter the Git branch names containing your changes. Ranch will look for Docker images built from these branches to overlay onto the baseline services.'
            }
          >
            <span style={{ cursor: 'help' }}>{isPrMode ? 'Pull Request URLs' : 'Feature Branches'}</span>
          </Popover>
        }
        isRequired
        fieldId="preview-entries-input"
      >
        <TextInput
          ref={inputRef}
          id="preview-entries-input"
          value={inputValue}
          onChange={(_e, v) => setInputValue(v)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              addEntry(inputValue);
            }
          }}
          placeholder={isPrMode
            ? 'https://github.com/Teladoc/service-name/pull/123'
            : 'e.g. feature/my-branch'}
          aria-label={isPrMode ? 'PR URL input' : 'Branch name input'}
        />

        {/* Chips */}
        {entries.length > 0 && (
          <ChipGroup style={{ marginTop: 8 }}>
            {entries.map((entry, idx) =>
              entry.kind === 'pr' ? (
                <PrChip
                  key={idx}
                  url={entry.url}
                  onResolve={(branch) => handleResolve(idx, branch)}
                  onRemove={() => removeEntry(idx)}
                  onError={handlePrError}
                />
              ) : (
                <Chip key={idx} onClick={() => removeEntry(idx)}>
                  {entry.name}
                </Chip>
              ),
            )}
          </ChipGroup>
        )}

        <FormHelperText>
          <HelperText>
            <HelperTextItem>
              {isPrMode
                ? 'Paste a GitHub PR URL and press Enter to add it.'
                : 'Type a branch name and press Enter to add it.'}
            </HelperTextItem>
          </HelperText>
        </FormHelperText>

        {/* Mode toggle */}
        <Button
          variant="link"
          isInline
          style={{ marginTop: 2, fontSize: 13 }}
          onClick={() => switchMode(isPrMode ? 'branch' : 'pr')}
        >
          {isPrMode ? 'Use branch names instead' : 'Use PR URLs instead'}
        </Button>

        {/* Session expiry warning */}
        {prAuthError && (
          <Alert
            variant="warning"
            isInline
            style={{ marginTop: 8 }}
            title="Session expired"
          >
            Your session has expired after a period of inactivity. Please reload the page to continue.
          </Alert>
        )}

        {/* Duplicate repo warning */}
        {duplicateRepos.length > 0 && (
          <Alert
            variant="warning"
            isInline
            style={{ marginTop: 8 }}
            title="Multiple PRs from the same repository"
          >
            {duplicateRepos.map(repo => (
              <div key={repo}>
                <strong>{repo}</strong> — only one PR's image can be deployed per service.
                The first PR with a built image will be used; the other will be ignored.
                To test both, merge them into a single branch.
              </div>
            ))}
          </Alert>
        )}
      </FormGroup>

      {/* ---- Namespace ---- */}
      <FormGroup
        label={
          <Popover
            triggerAction="hover"
            headerContent="Target Namespace"
            bodyContent="The Kubernetes namespace where the baseline services run. Your preview pod overlays on services in this namespace — traffic routed with your preview tag hits your pod instead of the live service."
          >
            <span style={{ cursor: 'help' }}>Target Namespace</span>
          </Popover>
        }
        isRequired
        fieldId="preview-namespace"
      >
        {loadingNs ? (
          <Spinner size="sm" />
        ) : (
          <BranchTypeahead
            value={values.namespace}
            onSelect={(v) => onChange({ ...values, namespace: v })}
            label="Namespace"
            branchOptions={namespaceOptions}
            placeholder="e.g. core-baseline, peds-staging"
            noResultText="No namespaces match"
          />
        )}
      </FormGroup>

      {/* ---- Preview Tag ---- */}
      <FormGroup
        label={
          <Popover
            triggerAction="hover"
            headerContent="Preview Tag"
            bodyContent="A short unique identifier for your session (e.g. your ticket number). It becomes the mirrord routing header value — only HTTP requests carrying this tag in the Baggage header are intercepted by your preview pod. Must be unique per namespace."
          >
            <span style={{ cursor: 'help' }}>Preview Tag</span>
          </Popover>
        }
        isRequired
        fieldId="preview-tag"
      >
        <TextInput
          id="preview-tag"
          value={values.previewTag}
          onChange={(_e, v) => onChange({ ...values, previewTag: v })}
          placeholder="e.g. nuc-1035"
          validated={tagValidated}
        />
        <FormHelperText>
          <HelperText>
            {checking && (
              <HelperTextItem variant="indeterminate">
                <Spinner size="sm" /> Checking uniqueness…
              </HelperTextItem>
            )}
            {!checking && inUse === true && (
              <HelperTextItem variant="error">
                Tag "{values.previewTag}" is already in use on this namespace.
              </HelperTextItem>
            )}
            {!checking && inUse === false && values.previewTag && (
              <HelperTextItem variant="success">Tag is available.</HelperTextItem>
            )}
            {!checking && inUse === undefined && (
              <HelperTextItem>Short identifier used as the routing header value.</HelperTextItem>
            )}
          </HelperText>
        </FormHelperText>
        {!checking && inUse === true && (
          <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
              <Checkbox
                id="preview-force-override"
                isChecked={values.forceOverride}
                onChange={(_e, checked) => onChange({ ...values, forceOverride: checked })}
                style={{ marginTop: 2 }}
              />
              <label htmlFor="preview-force-override" style={{ cursor: 'pointer', lineHeight: '1.4' }}>
                <span style={{ fontWeight: 500 }}>Override existing session</span>
                <span style={{ display: 'block', fontSize: 13, color: 'var(--pf-v5-global--Color--200, #6a6e73)', marginTop: 2 }}>
                  Redeploy with your current changes — the existing preview pod for this tag will be replaced.
                </span>
              </label>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <Button
                variant="danger"
                size="sm"
                isLoading={isStopping}
                isDisabled={isStopping}
                onClick={async () => {
                  const ok = await stopPreview(values.namespace, values.previewTag);
                  if (ok) setStopRefreshKey(k => k + 1);
                }}
              >
                Stop Preview
              </Button>
              <span style={{ fontSize: 13, color: 'var(--pf-v5-global--Color--200, #6a6e73)' }}>
                Terminate the running session and free this tag.
              </span>
            </div>
            {stopError && (
              <HelperText>
                <HelperTextItem variant="error">{stopError}</HelperTextItem>
              </HelperText>
            )}
          </div>
        )}
      </FormGroup>

      {/* ---- TTL ---- */}
      <FormGroup
        label={
          <Popover
            triggerAction="hover"
            headerContent="TTL (Time To Live)"
            bodyContent="How long the preview session stays alive before it is automatically torn down. Use a number followed by h for hours or d for days (e.g. 4h, 1d). The session and all its resources are cleaned up after this time."
          >
            <span style={{ cursor: 'help' }}>TTL</span>
          </Popover>
        }
        isRequired
        fieldId="preview-ttl"
      >
        <TextInput
          id="preview-ttl"
          value={values.ttl}
          onChange={(_e, v) => onChange({ ...values, ttl: v })}
          placeholder="e.g. 4h or 1d"
          validated={ttlValidated}
        />
        <FormHelperText>
          <HelperText>
            {ttlValidated === ValidatedOptions.error ? (
              <HelperTextItem variant="error">
                Use a number followed by h (hours) or d (days), e.g. 4h or 2d.
              </HelperTextItem>
            ) : (
              <HelperTextItem>How long the preview session lives before auto-teardown.</HelperTextItem>
            )}
          </HelperText>
        </FormHelperText>
      </FormGroup>

      {/* ---- Regression Tests ---- */}
      <FormGroup label="Regression Tests" fieldId="preview-regression-tests-enabled">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Switch
            id="preview-regression-tests-enabled"
            label="Run regression tests after deploy"
            isChecked={values.regressionTestsEnabled}
            onChange={(_e, checked) => {
              onChange({ ...values, regressionTestsEnabled: checked });
              if (checked) setIsRegressionModalOpen(true);
            }}
          />
          {values.regressionTestsEnabled && (
            <Button variant="link" isInline onClick={() => setIsRegressionModalOpen(true)}>
              Edit test configuration
            </Button>
          )}
        </div>
      </FormGroup>

      <RegressionTestConfigModal
        isOpen={isRegressionModalOpen}
        onClose={() => setIsRegressionModalOpen(false)}
        selections={values.regressionTestSelections}
        onChange={(next) => onChange({ ...values, regressionTestSelections: next })}
      />
    </Form>
  );
};
